import { motion } from 'motion/react';
import { useEffect, useRef, useState, type ReactElement } from 'react';

/**
 * Top-right route-change toast.
 *
 * Replaces the older centered overlay loader. The toast doesn't dim
 * the page or block input — it just lives in the corner and reports
 * progress so the user knows something's happening without losing the
 * underlying skeleton context.
 *
 * Counts requests issued since the loader mounted via
 * `performance.getEntriesByType('resource')`, splits into completed
 * vs in-flight, and renders the ratio + a thin progress bar.
 *
 * Mobile: anchors to the top edge with side padding so it doesn't
 * collide with the safe-area inset or hover under the status bar.
 */

interface AssetProgress {
  loaded: number;
  total: number;
}

function useAssetProgress(): AssetProgress {
  const [progress, setProgress] = useState<AssetProgress>({
    loaded: 0,
    total: 0,
  });
  const peakTotalRef = useRef(0);

  useEffect(() => {
    if (typeof performance === 'undefined') return undefined;

    const baselineCount = performance.getEntriesByType('resource').length;

    const tick = (): void => {
      const all = performance.getEntriesByType(
        'resource'
      ) as PerformanceResourceTiming[];
      const sinceMount = all.slice(baselineCount);

      const total = sinceMount.length;
      const loaded = sinceMount.filter((r) => r.responseEnd > 0).length;

      // Monotonic peak so the bar never jitters backwards if the
      // browser drops entries from the resource buffer.
      if (total > peakTotalRef.current) {
        peakTotalRef.current = total;
      }

      setProgress({ loaded, total: peakTotalRef.current });
    };

    tick();
    const interval = window.setInterval(tick, 120);
    return () => window.clearInterval(interval);
  }, []);

  return progress;
}

function formatProgress({ loaded, total }: AssetProgress): string {
  if (total === 0) return 'Preparing…';
  return `${loaded} / ${total}`;
}

function progressPercent({ loaded, total }: AssetProgress): number {
  if (total === 0) return 8;
  return Math.min(100, Math.round((loaded / total) * 100));
}

export function RouteLoader(): ReactElement {
  const progress = useAssetProgress();
  const pct = progressPercent(progress);

  return (
    <motion.div
      aria-live="polite"
      aria-label="Loading page"
      role="status"
      initial={{ opacity: 0, y: -12, x: 0 }}
      animate={{ opacity: 1, y: 0, x: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
      className={[
        // Position — top-right on tablet+, near the corner. On mobile
        // the toast sits below the safe-area top inset, hugging the
        // right edge but constrained to a comfortable width.
        'pointer-events-none fixed right-3 top-3 z-[60] sm:right-4 sm:top-4',
        'flex max-w-[calc(100vw-1.5rem)] sm:max-w-xs',
        'select-none',
      ].join(' ')}
    >
      <div
        className={[
          'flex w-full items-center gap-3 rounded-full border border-border/60 bg-card/95',
          'px-3 py-2 shadow-lg backdrop-blur-md',
          'sm:gap-3 sm:px-3.5 sm:py-2',
        ].join(' ')}
      >
        <motion.img
          src="/logo.svg"
          alt="1Anime"
          width={20}
          height={20}
          className="h-5 w-5 flex-shrink-0 select-none"
          draggable={false}
          animate={{ rotate: 360 }}
          transition={{
            duration: 1.4,
            repeat: Infinity,
            ease: 'linear',
          }}
        />

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-baseline justify-between gap-2 text-[11px] font-medium leading-none">
            <span className="truncate text-foreground/85">Loading</span>
            <span className="flex-shrink-0 tabular-nums text-muted-foreground">
              {formatProgress(progress)}
            </span>
          </div>
          <div className="h-1 w-full overflow-hidden rounded-full bg-muted/70">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-primary/80 via-primary to-primary/80"
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default RouteLoader;
